/**
 * @typedef {Object} PlanDetails
 * @property {string} code
 * @property {number} monthlyCost
 * @property {number} [annualCost]
 * @property {number} setupFee
 * @property {string} customerType
 * @property {string} planName
 * @property {string} capacityLabel
 * @property {number} capacityAmount
 * @property {string} [secondaryCapacityLabel]
 * @property {number} [secondaryCapacityAmount]
 */

export const CUSTOMER_TYPES = {
  LENDER: 'Lender',
  REAL_ESTATE_AGENT: 'Real Estate Agent',
  INSURANCE_AGENT: 'Insurance Agent',
  TITLE_AGENT: 'Title Agent',
};

export const PLAN_NAMES = {
  STARTER: 'Starter',
  INDIVIDUAL: 'Individual',
  PARTNER: 'Partner',
  TEAMS: 'Teams',
  FREEMIUM_STARTER: 'Freemium Starter',
  PRO: 'Pro',
  UNLIMITED: 'Unlimited'
};

/** @type {Object.<string, PlanDetails>} */
const planData = {
  'hb-lender-starter': {
    code: 'hb-lender-starter',
    monthlyCost: 125,
    annualCost: 1250,
    setupFee: 100,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: PLAN_NAMES.STARTER,
    capacityLabel: 'Homeowners',
    capacityAmount: 100,
    secondaryCapacityLabel: 'buyers',
    secondaryCapacityAmount: 100
  },
  'hb-lender-pro': {
    code: 'hb-lender-pro',
    monthlyCost: 225,
    annualCost: 2250,
    setupFee: 100,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: PLAN_NAMES.INDIVIDUAL,
    capacityLabel: 'Homeowners',
    capacityAmount: 500,
    secondaryCapacityLabel: 'buyers',
    secondaryCapacityAmount: 1500
  },
  'hb-lender-unlimited': {
    code: 'hb-lender-unlimited',
    monthlyCost: 300,
    annualCost: 3000,
    setupFee: 100,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: PLAN_NAMES.UNLIMITED,
    capacityLabel: 'Active Portal Users',
    capacityAmount: 500,
    secondaryCapacityLabel: 'clients',
    secondaryCapacityAmount: 'Unlimited'
  },
  'hb-agent-solo': {
    code: 'hb-agent-solo',
    monthlyCost: 50,
    setupFee: 50,
    customerType: CUSTOMER_TYPES.REAL_ESTATE_AGENT,
    planName: PLAN_NAMES.STARTER,
    capacityLabel: 'homebuyers',
    capacityAmount: 250
  },
  'hb-agent-partner': {
    code: 'hb-agent-partner',
    monthlyCost: 25,
    setupFee: 50,
    customerType: CUSTOMER_TYPES.REAL_ESTATE_AGENT,
    planName: PLAN_NAMES.PARTNER,
    capacityLabel: 'homebuyers',
    capacityAmount: 250
  },
  'hb-agent-team': {
    code: 'hb-agent-team',
    monthlyCost: 100,
    setupFee: 50,
    customerType: CUSTOMER_TYPES.REAL_ESTATE_AGENT,
    planName: PLAN_NAMES.TEAMS,
    capacityLabel: 'homebuyers',
    capacityAmount: 500
  },
  'freemium-starter': {
    code: 'freemium-starter',
    monthlyCost: 0,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.REAL_ESTATE_AGENT,
    planName: PLAN_NAMES.FREEMIUM_STARTER,
    capacityLabel: 'homebuyers',
    capacityAmount: 25
  },
  'hb-insurance-agent-pro': {
    code: 'hb-insurance-agent-pro',
    monthlyCost: 200,
    setupFee: 200,
    customerType: CUSTOMER_TYPES.INSURANCE_AGENT,
    planName: PLAN_NAMES.PRO,
    capacityLabel: 'contacts',
    capacityAmount: 2000
  },
  'hb-title-agent-pro': {
    code: 'hb-title-agent-pro',
    monthlyCost: 200,
    setupFee: 200,
    customerType: CUSTOMER_TYPES.TITLE_AGENT,
    planName: PLAN_NAMES.PRO,
    capacityLabel: 'contacts',
    capacityAmount: 500
  },
  'fairway-unlimited-pro': {
    code: 'fairway-unlimited-pro',
    monthlyCost: 180,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Fairway Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'fairway-unlimited-starter': {
    code: 'fairway-unlimited-starter',
    monthlyCost: 135,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Fairway Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      'Unlimited Clients & Email Engagement',
      '100 Actively Engaged Clients'
    ]
  },
  'edge-preferred-broker-pro': {
    code: 'edge-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Edge Lender Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'edge-preferred-broker-starter': {
    code: 'edge-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Edge Lender Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'vip-preferred-broker-pro': {
    code: 'vip-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'VIP Mortgage Preferred Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'vip-preferred-broker-starter': {
    code: 'vip-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'VIP Mortgage Preferred Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'evergreen-preferred-broker-pro': {
    code: 'evergreen-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Evergreen Home Loans Preferred Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'evergreen-preferred-broker-starter': {
    code: 'evergreen-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Evergreen Home Loans Preferred Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'west-preferred-broker-pro': {
    code: 'west-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'West Capital Lender Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'west-preferred-broker-starter': {
    code: 'west-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'West Capital Lender Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'union-preferred-broker-pro': {
    code: 'union-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Union Preferred Broker Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'union-preferred-broker-starter': {
    code: 'union-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Union Preferred Broker Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'rocket-preferred-broker-pro': {
    code: 'rocket-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Rocket Preferred Broker Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'rocket-preferred-broker-starter': {
    code: 'rocket-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Rocket Preferred Broker Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'barrett-preferred-broker-pro': {
    code: 'barrett-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Barrett Preferred Broker Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'barrett-preferred-broker-starter': {
    code: 'barrett-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Barrett Preferred Broker Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'arbor-financial-group-preferred-broker-pro': {
    code: 'arbor-financial-group-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Arbor Financial Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'arbor-financial-group-preferred-broker-starter': {
    code: 'arbor-financial-group-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Arbor Financial Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'c2-preferred-broker-pro': {
    code: 'c2-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'C2 Preferred Broker Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'c2-preferred-broker-starter': {
    code: 'c2-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'C2 Preferred Broker Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'xpert-preferred-broker-pro': {
    code: 'xpert-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Xpert Lender Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'xpert-preferred-broker-starter': {
    code: 'xpert-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Xpert Lender Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'emortgage-preferred-broker-pro': {
    code: 'emortgage-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'E Mortgage Capital Lender Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'emortgage-preferred-broker-starter': {
    code: 'emortgage-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'E Mortgage Capital Lender Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'all-western-preferred-broker-pro': {
    code: 'all-western-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'All Western Lender Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'all-western-preferred-broker-starter': {
    code: 'all-western-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'All Western Lender Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'ccm-preferred-imb-pro': {
    code: 'ccm-preferred-imb-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'CCM Preferred IMB Lender Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'umortgage-preferred-broker-pro': {
    code: 'umortgage-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'UMortgage Lender Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'umortgage-preferred-broker-starter': {
    code: 'umortgage-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'UMortgage Lender Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'revolution-preferred-broker-pro': {
    code: 'revolution-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Revolution Mortgage Lender Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'revolution-preferred-broker-starter': {
    code: 'revolution-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Revolution Mortgage Lender Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'newrez-preferred-imb-pro': {
    code: 'newrez-preferred-imb-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Newrez Preferred IMB Lender Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'apm-individual': {
    code: 'apm-individual',
    monthlyCost: 0,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'American Pacific Mortgage',
    capacityLabel: 'Clients',
    capacityAmount: 500,
    features: [
      '500 clients · 2,000 prospects — $75/month billed to your branch',
      '1,000 clients · 4,000 prospects — $75/month',
      '2,000 clients · 8,000 prospects — $125/month',
      'Additional blocks of 1,000 clients — $50/month per block'
    ],
    acknowledgments: [
      {
        key: 'billing',
        label: 'I acknowledge that $75 will be billed back to my branch monthly and this has been approved by my branch manager',
        errorMessage: 'You must acknowledge the billing arrangement'
      },
      {
        key: 'creditCard',
        label: 'I acknowledge that a credit card is required to begin client importing and if my billable client count exceeds 500, I will be charged to the card on file',
        errorMessage: 'You must acknowledge the credit card requirement'
      }
    ]
  }
};

/**
 * Mapping of corporate profile IDs to plan codes
 * Can be a single plan code (string) or multiple plan codes (array)
 * @type {Object.<string, string|string[]>}
 */
export const companyIdToPlanMap = {
  // '00810f49-666e-4294-8a8c-9a9feec5c622': ['fairway-unlimited-pro', 'fairway-unlimited-starter'], // swap to local company id for testing
  '218d90e3-3f41-4000-b6ae-467edfc4e929': ['fairway-unlimited-pro', 'fairway-unlimited-starter'],
  '9037c833-17c2-43fe-aec3-ae4c43cecf6c': ['edge-preferred-broker-pro', 'edge-preferred-broker-starter'],
  'a9d52fd4-5025-4747-8327-58c4e56b3149': ['evergreen-preferred-broker-pro', 'evergreen-preferred-broker-starter'],
  '65d85af7-bdce-485e-b60a-d290bf090807': ['west-preferred-broker-pro', 'west-preferred-broker-starter'],
  '32b78cdb-ad7c-4a35-b2b4-b8a41cebf41e': ['union-preferred-broker-pro', 'union-preferred-broker-starter'],
  'b92d17aa-68dd-49c9-8097-583926507d62': ['rocket-preferred-broker-pro', 'rocket-preferred-broker-starter'],
  'bd5ca4d1-858d-4535-abb8-89e1164e0344': ['barrett-preferred-broker-pro', 'barrett-preferred-broker-starter'],
  'e1e7c655-735a-4b71-ad70-f1e1a7886967': ['arbor-financial-group-preferred-broker-pro', 'arbor-financial-group-preferred-broker-starter'],
  '14b5e174-cb5d-4334-957e-e0f32e97a93a': ['c2-preferred-broker-pro', 'c2-preferred-broker-starter'],
  'd37bbffd-2066-4f89-ac9d-adc63c2dba17': ['xpert-preferred-broker-pro', 'xpert-preferred-broker-starter'],
  '42447d70-03ad-44e5-9999-161445d16f35': ['emortgage-preferred-broker-pro', 'emortgage-preferred-broker-starter'],
  '8421f384-df64-4ae0-941d-3e60f4ad88a0': ['vip-preferred-broker-pro', 'vip-preferred-broker-starter'],
  '9fde629f-30ad-42a9-8133-8ad3b305fb7c': ['all-western-preferred-broker-pro', 'all-western-preferred-broker-starter'],
  '158ed150-82fa-42f2-969c-7ef34b939ebd': ['umortgage-preferred-broker-pro', 'umortgage-preferred-broker-starter'],
  '16aa53d4-1862-4944-b250-dc33ca458f4a': ['ccm-preferred-imb-pro'],
  '814f099d-2604-48a2-8a26-dfd41d59ff19': ['revolution-preferred-broker-pro', 'revolution-preferred-broker-starter'],
  'f6730666-cd94-4e1a-b300-de1e370ba15d': ['newrez-preferred-imb-pro'],
  '7c7eaa86-e792-469f-91ee-d9597719795c': ['apm-individual']
};

/**
 * @param {string} planCode
 * @returns {PlanDetails|null}
 */
export const getPlanDetails = (planCode) => {
  return planData[planCode] || null;
};

/**
 * Get plan code(s) for a given company ID
 * @param {string} companyId
 * @returns {string[]|null} - Always returns an array of plan codes or null
 */
export const getPlanCodesForCompany = (companyId) => {
  const plans = companyIdToPlanMap[companyId];
  if (!plans) return null;
  // Always return as array for consistency
  return Array.isArray(plans) ? plans : [plans];
};

export const plans = Object.values(planData);
